import { Auth0Client } from '@auth0/auth0-spa-js';
import axios from 'axios';
import store from '../store';
import * as AuthReducer from '../reducers/authReducer';
import { setError } from '../reducers/uiReducer';
import StorageService from './StorageService';

const INTERACTIVE_AUTH_ERRORS = ['mfa_required', 'login_required', 'interaction_required', 'consent_required'];
// How long to assume a loginWithRedirect navigation is in flight before allowing another attempt.
const REDIRECT_FLAG_RESET_MS = 10000;
// The SDK default is 60s. The silent-auth iframe only ever takes that long when it can never
// post back (origin not in Allowed Web Origins, third-party cookies blocked), so fail fast.
const AUTHORIZE_TIMEOUT_SECONDS = 10;

class AuthService {
  auth0 = null;
  isInitialized = false;
  history = null;
  cachedAccessToken = null; // Cache current token to avoid unnecessary localStorage writes
  isRedirectingForAuth = false;
  forceRefreshPromise = null;

  setHistory = (history) => {
    this.history = history;
  }

  init = async () => {
    if (this.isInitialized) return;

    // createAuth0Client() is `new Auth0Client()` followed by an awaited checkSession(). That check
    // falls back to a hidden authorize iframe when there is no refresh token in the cache, and a
    // non-recoverable failure there (notably a timeout) rejected the whole init and left the app
    // on the loader for up to a minute. Construct the client ourselves and make the check best-effort.
    this.auth0 = new Auth0Client({
      domain: process.env.REACT_APP_AUTH0_DOMAIN,
      client_id: process.env.REACT_APP_AUTH0_CLIENT_ID,
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      redirect_uri: `${window.location.origin}/callback`,
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      authorizeTimeoutInSeconds: AUTHORIZE_TIMEOUT_SECONDS,
    });
    this.isInitialized = true;

    try {
      await this.auth0.checkSession();
    } catch (err) {
      // Not fatal: getTokenSilently on the first request (or loginWithRedirect on /auth) will
      // establish the session. login_required etc. are already swallowed by the SDK here.
      console.warn('Auth0 session check failed, continuing without it:', err?.error || err); // eslint-disable-line no-console
    }

    if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
      try {
        await this.auth0.handleRedirectCallback();

        const accessToken = await this.auth0.getTokenSilently();
        const idTokenClaims = await this.auth0.getIdTokenClaims();
        const idToken = idTokenClaims?.__raw;

        if (accessToken) {
          this.updateAccessToken(accessToken);
        }
        if (idToken) {
          this.updateIdToken(idToken);
        }

        StorageService.createEntry('authType', 'auth0');

        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

        const path = await store.dispatch(AuthReducer.loggedIn());

        if (this.history) {
          this.history.push(path || '/');
        } else {
          window.location.href = path || '/';
        }
      } catch (err) {
        console.error('Error handling redirect callback:', err);
        store.dispatch(setError(err));

        // Clear the half-built session so the next load does not try to log out again,
        // then land on /auth where the message is shown instead of auto-redirecting.
        this.clearSession();

        window.location.href = '/auth?logout_message=auth0error';
      }
    } else if (window.location.search.includes('error=')) {
      const params = new URLSearchParams(window.location.search);
      const error = params.get('error');
      const errorDescription = params.get('error_description');

      window.history.replaceState({}, document.title, window.location.pathname);

      let logoutMessage = 'auth0error';
      if (error === 'access_denied') {
        logoutMessage = 'access_denied';
      } else if (error === 'unauthorized') {
        logoutMessage = 'user_blocked';
      } else if (errorDescription?.toLowerCase().includes('blocked')) {
        logoutMessage = 'user_blocked';
      } else if (error === 'login_required') {
        logoutMessage = 'login_required';
      } else if (error === 'consent_required') {
        logoutMessage = 'consent_required';
      }

      this.clearSession();
      // Only Auth0 redirects carry `error=`, so these are Auth0 users: show the message on /auth
      // (which renders a Log In button when a message is present) rather than the legacy form.
      window.location.replace(`/auth?logout_message=${logoutMessage}`);
    } else {
      // Load cached token on init
      const token = localStorage.getItem('access_token');
      if (token) {
        this.cachedAccessToken = token;
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      }
    }
  }

  /**
   * Start the Auth0 redirect login.
   * @param {string} [errorMessage] forwarded to Auth0 as `logout_message` for the Universal Login template
   * @param {object} [options]
   * @param {boolean} [options.forceLogin] send `prompt=login` so Auth0 shows the credential form even
   *   when it still holds a session. Used when retrying from an error page: after a post-login
   *   denial Auth0 keeps the session for the denied account, so without this the retry silently
   *   re-runs the same denial and the user can never switch accounts.
   */
  login = async (errorMessage, { forceLogin = false } = {}) => {
    await this.init();
    let connection = StorageService.readEntry('auth-connection');
    connection = ((connection !== 'all') ? connection : undefined);

    if (!this.auth0) throw new Error('Auth0 client not initialized');

    return this.auth0.loginWithRedirect({
      redirect_uri: `${window.location.origin}/callback`,
      scope: 'openid profile email offline_access',
      ...(connection && { connection }),
      ...(errorMessage && { logout_message: errorMessage }),
      ...(forceLogin && { prompt: 'login' }),
    });
  }

  getAccessToken = async () => {
    await this.init();
    try {
      // The SDK checks expiry, refreshes with the refresh token when needed, dedupes concurrent
      // calls and holds a cross-tab lock, so this is cheap on the cached path.
      const token = await this.auth0.getTokenSilently();

      // Only touch storage (and only read the id token) when the access token actually rolled over.
      if (this.updateAccessToken(token)) {
        await this.syncIdToken();
      }

      return token;
    } catch (err) {
      return this.handleTokenError(err);
    }
  }

  /**
   * Bypass the SDK cache and hit the token endpoint with the refresh token. Used by the 401 retry,
   * where the cached token is by definition the one the API just rejected. Concurrent callers
   * share one in-flight refresh so a burst of 401s does not become a burst of refreshes.
   */
  forceRefresh = async () => {
    await this.init();
    if (!this.forceRefreshPromise) {
      this.forceRefreshPromise = this.auth0.getTokenSilently({ ignoreCache: true })
        .then(async (token) => {
          if (this.updateAccessToken(token)) {
            await this.syncIdToken();
          }
          return token;
        })
        .catch((err) => this.handleTokenError(err))
        .finally(() => {
          this.forceRefreshPromise = null;
        });
    }
    return this.forceRefreshPromise;
  }

  syncIdToken = async () => {
    const idTokenClaims = await this.auth0.getIdTokenClaims();
    const idToken = idTokenClaims?.__raw;
    if (idToken) {
      this.updateIdToken(idToken);
    }
  }

  /**
   * Interactive errors (mfa_required, login_required, ...) cannot be resolved silently: send the
   * user through the normal login entry point (so `connection` is applied consistently) and
   * resolve null so callers treat it as "no token". Anything else is rethrown.
   */
  handleTokenError = (err) => {
    if (INTERACTIVE_AUTH_ERRORS.includes(err.error)) {
      if (!this.isRedirectingForAuth) {
        this.isRedirectingForAuth = true;
        const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (!currentPath.includes('/callback') && !currentPath.includes('/auth') && !currentPath.includes('/login')) {
          sessionStorage.setItem('redirect_url', currentPath);
        }

        this.login()
          .catch((loginErr) => console.error('Login redirect failed:', loginErr))
          .finally(() => {
            // loginWithRedirect normally navigates away. If it did not (blocked, offline), release
            // the flag so a later request can try again instead of silently returning null forever.
            setTimeout(() => { this.isRedirectingForAuth = false; }, REDIRECT_FLAG_RESET_MS);
          });
      }
      return null;
    }
    console.error('Error getting access token:', err);
    throw err;
  }

  getIdToken = async () => {
    await this.init();
    return (await this.auth0.getIdTokenClaims())?.__raw;
  }

  /**
   * Update access token in localStorage only if it has changed
   * @param {string} newToken - The new access token
   */
  updateAccessToken(newToken) {
    if (newToken && newToken !== this.cachedAccessToken) {
      localStorage.setItem('access_token', newToken);
      this.cachedAccessToken = newToken;
      axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
      return true;
    }
    return false;
  }

  /**
   * Update ID token in localStorage only if it has changed
   * @param {string} newToken - The new ID token
   */
  updateIdToken(newToken) {
    const currentIdToken = localStorage.getItem('id_token');
    if (newToken && newToken !== currentIdToken) {
      localStorage.setItem('id_token', newToken);
      return true;
    }
    return false;
  }

  /**
   * Clear all tokens from cache and storage
   */
  clearTokens() {
    this.cachedAccessToken = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('authType');
    delete axios.defaults.headers.common.Authorization;
  }

  /**
   * Clear tokens plus the app-level session markers (user/org/authType/...).
   * Use this on every error path that sends the user back to /auth; clearing only the tokens
   * leaves `user` behind and App.getInitialView then dispatches another logout on the next load.
   */
  clearSession() {
    this.clearTokens();
    ['user', 'org', 'authType', 'multiOrgs', 'patientExperience', 'subscriptions'].forEach((key) => StorageService.destroyEntry(key));
    ['redirect_url', 'axiumLogin', 'activePatientId'].forEach((key) => sessionStorage.removeItem(key));
  }

  logout = async (logoutType) => {
    try {
      await this.init();

      this.clearTokens();

      let params = '';
      if (logoutType) params = `?logout_message=${logoutType}`;

      if (!this.auth0) {
        console.error('Auth0 client not initialized, redirecting manually');
        window.location.href = `${window.location.origin}/auth${params}`;
        return;
      }

      await this.auth0.logout({
        returnTo: `${window.location.origin}/auth${params}`,
      });
    } catch (err) {
      console.error('Logout error:', err);
      this.clearSession();

      let params = '';
      if (logoutType) params = `?logout_message=${logoutType}`;
      window.location.href = `${window.location.origin}/auth${params}`;
    }
  }
}

export default new AuthService();
